import { useCallback, useEffect, useMemo, useState } from "react";
import type { LimitOffsetPaginationResponse } from "../../../api/src/lib";
import { useAuthenticatedFetch } from "./api";

/**
 * The `limit` a firm screen opens its walk with, so the whole collection
 * arrives in one page.
 *
 * None of the five screens that walk a firm collection — `useFirmRiskProfiles`,
 * `MinimumTradeSizes.tsx`, `RiskAllocationsAssetClass.tsx`,
 * `SecurityCategoryAssetClass.tsx` and `SecurityMaster.tsx` — offers the reader
 * a page control. Each shows the collection entire and reaches it by asking for
 * page after page until the sibling key's total is met. Asking for everything
 * on the first request collapses that to a single round trip. The server
 * applies whatever `limit` it is sent, so this is the size it uses.
 *
 * It stands until those screens are rewritten in the UI build to page in the
 * interface. The walk itself is unchanged and still runs: a response that
 * reports a smaller `pagination.limit` than was asked for is still followed,
 * page by page, on the size the server named.
 *
 * `api/src/rebalances/rebalances.controller.ts:139-144` reads a whole
 * collection the same way, with a hardcoded `limit` of its own.
 */
export const CONSOLE_PAGE_SIZE = 99999999;

/** What a paginated read answers with: a page of rows and the pagination beside it. */
type PageResponse<TRow> = {
  data: TRow[];
  /**
   * Optional even though a paginated route always sends it. A response without
   * one is not a page this walk can advance through, and treating its absence
   * as "there is no more" is the reading that cannot silently truncate.
   */
  pagination?: LimitOffsetPaginationResponse;
};

/**
 * One page the walk has collected, plus the offset it was asked for.
 *
 * `requestedOffset` is carried rather than read back off `pagination` because
 * it is what the walk de-duplicates on: it is known before the response
 * arrives, so a response reporting some other offset cannot make one page be
 * collected twice.
 */
type CollectedPage<TRow> = {
  requestedOffset: number;
  data: TRow[];
  pagination?: LimitOffsetPaginationResponse;
};

/**
 * Every row of a paginated collection, walked a page at a time through
 * `useAuthenticatedFetch`.
 *
 * `path` is the API path without a query string. The walk appends `limit` and
 * `offset` itself, and `path` is also the FIRST element of every react-query
 * key the walk uses, so a caller that invalidates `[path]` after a save
 * reaches every page of the collection.
 *
 * WHY THE WALK IS WRITTEN ONCE
 *
 * A screen that read the first page of a collection and treated it as all of
 * it would silently drop every row on the rest, and `tsc` cannot see it
 * because a page and the whole collection have the same element type. One
 * walk, written once, is what keeps the screens that read a paginated route
 * from drifting apart.
 *
 * `query` carries a filter the route takes beside `limit` and `offset`, such as
 * the `status` of `GET /v1/transfers/forecast`. It is written into every url
 * ahead of `limit` and `offset`, and added to every react-query key after the
 * offset, so an invalidation of `[path]` still reaches every page, and two
 * walks of one path under different filters never share a page.
 *
 * The rows collected are held per mounted hook. A screen whose `path` or
 * `query` can change while it stays mounted remounts the hook on the change
 * (a React `key`), so the rows of one walk are never carried into the next.
 *
 * `enabled: false` holds the walk before its first request, as react-query's
 * `enabled` does for one query: a reader that needs the collection only in
 * some states (`useQueryHouseholds` in `clients/household/lib.ts`) asks for
 * nothing until it does. While it is `false`, `hasEveryRow` stays `false`.
 *
 * `reload` restarts the walk, for a screen that WRITES to the collection.
 * Invalidating the query key is not enough on its own: the walk stops asking
 * once it holds everything, so an invalidation marks entries stale that nothing
 * is subscribed to any more and refetches nothing. Read-only screens never call
 * it.
 */
export function useAuthenticatedFetchAllPages<TRow>(
  path: string,
  query: Record<string, string> = {},
  { enabled = true }: { enabled?: boolean } = {},
) {
  const [collectedPages, setCollectedPages] = useState<CollectedPage<TRow>[]>(
    [],
  );

  const rows = useMemo(
    () => collectedPages.flatMap((page) => page.data ?? []),
    [collectedPages],
  );

  const lastPage =
    collectedPages.length === 0
      ? undefined
      : collectedPages[collectedPages.length - 1];

  // Every row is in hand when the sibling key's total has been reached, when a
  // page came back empty (which ends the walk rather than repeating it), or
  // when the response carried no pagination at all.
  const hasEveryRow =
    lastPage !== undefined &&
    (lastPage.pagination === undefined ||
      (lastPage.data ?? []).length === 0 ||
      rows.length >= lastPage.pagination.totalCount);

  // The FIRST page asks for CONSOLE_PAGE_SIZE, so the collection arrives whole
  // and the walk ends after one response. Every page AFTER it starts where the
  // rows in hand end and asks for the size the SERVER named on the page before
  // — a server that answered with fewer rows than were asked for is followed by
  // the number it reported rather than by the one it was sent, so the walk
  // still advances correctly if the collection is ever bounded again.
  const nextOffset = rows.length;
  const filter = new URLSearchParams(query).toString();
  const filterPrefix = filter === "" ? "" : `${filter}&`;
  const nextPageUrl =
    lastPage?.pagination === undefined
      ? `${path}?${filterPrefix}limit=${CONSOLE_PAGE_SIZE}&offset=${nextOffset}`
      : `${path}?${filterPrefix}limit=${lastPage.pagination.limit}&offset=${nextOffset}`;

  const { isPending, isFetching, isError, data, refetch } =
    useAuthenticatedFetch<PageResponse<TRow>>(
      nextPageUrl,
      {},
      { enabled: enabled && !hasEveryRow },
      // The key is the PATH plus the offset, not the url: an invalidation after
      // a save matches on the path alone, and a key built from the query string
      // would leave each page in a cache compartment it never reaches. A filter
      // follows the offset, so the path alone still matches every page.
      filter === "" ? [path, nextOffset] : [path, nextOffset, filter],
    );

  useEffect(() => {
    // `hasEveryRow` is checked here and not only through `enabled`: a finished
    // walk must stop collecting whatever the hook hands back, or an answer that
    // arrives after the last page is taken for the next one and the walk never
    // ends.
    //
    // `data` can be a page react-query still holds from an earlier walk of this
    // path: the hook hands it back at once and asks for the page again. It is
    // collected only when that request has answered (`isFetching` is false and
    // `isError` is false), and never while `enabled` is false, so a walk never
    // shows the rows of the walk before it.
    if (!enabled) return;
    if (data === undefined) return;
    if (isFetching || isError) return;
    if (hasEveryRow) return;

    setCollectedPages((pages) =>
      pages.some((page) => page.requestedOffset === nextOffset)
        ? pages
        : [
            ...pages,
            {
              requestedOffset: nextOffset,
              data: data.data,
              pagination: data.pagination,
            },
          ],
    );
  }, [data, enabled, hasEveryRow, isError, isFetching, nextOffset]);

  return {
    /** Every row collected so far, as the route serialised it. */
    rows,
    /** True once the walk holds as many rows as the sibling key reports. */
    hasEveryRow,
    /** True only while the FIRST page is outstanding. */
    isPendingFirstPage:
      collectedPages.length === 0 && (isPending || isFetching),
    isError,
    /** True while a page request is in flight, including a retry after an error. */
    isFetching,
    /**
     * Ask again for the page whose request failed. The rows already collected
     * stay, so the walk resumes where it stopped.
     */
    retry: useCallback(() => void refetch(), [refetch]),
    /** Drop what has been collected, so the walk starts again at offset 0. */
    reload: useCallback(() => setCollectedPages([]), []),
  };
}
